import React, {useEffect} from 'react';

import cs from 'classnames';
import moment from 'moment-tz';
import {DateTimePickerProps} from 'react-flatpickr';

import {useDateRange} from 'app/general/dates/date-range';
import {SweetDatePicker} from 'app/general/widgets/SweetDatePicker';
import {gt} from 'app/utils/i18n';

import type {
  DateRangeType,
  VALID_RANGE_TEMPLATE,
} from 'app/general/dates/date-range';

interface Props extends Pick<DateTimePickerProps, 'options'> {
  initialValue: VALID_RANGE_TEMPLATE;
  onTemplateChange?: (template: VALID_RANGE_TEMPLATE) => unknown;
  onChange: (range: DateRangeType) => unknown;
  templates: Array<VALID_RANGE_TEMPLATE>;
  labelClass?: string;
  fieldWrapperClass?: string;
  label?: React.ReactNode;
}

export function SelectDateRangeForm({
  initialValue,
  onChange,
  templates,
  onTemplateChange = () => null,
  label,
  labelClass = 'col-12',
  fieldWrapperClass = 'col-12',
  options = {},
}: Props) {
  const {
    rangeTemplates,
    selectedRangeTemplate,
    selectRangeTemplate,
    customRange,
    selectCustomRange,
    getCustomRangeTitle,
    userTz,
  } = useDateRange({
    initialValue,
    templates,
  });

  useEffect(() => {
    onChange(customRange);
  }, [customRange]);

  useEffect(() => {
    onTemplateChange(selectedRangeTemplate);
  }, [selectedRangeTemplate]);

  return (
    <div>
      <div className="form-group row">
        <label
          htmlFor="export-activity-time-period-field"
          className={labelClass}
        >
          {label ?? gt('Time period')}
        </label>
        <div className={fieldWrapperClass}>
          <select
            id="export-activity-time-period-field"
            className="form-control"
            value={selectedRangeTemplate}
            onChange={(e) =>
              selectRangeTemplate(e.target.value as VALID_RANGE_TEMPLATE)
            }
          >
            {rangeTemplates.map((template) => (
              <option key={template.key} value={template.key}>
                {template.label()}
              </option>
            ))}
          </select>
        </div>
      </div>
      {selectedRangeTemplate === 'custom' ? (
        <div className="form-group row">
          <label htmlFor="custom-date-range-field" className={labelClass}>
            {gt('Select Date Range')}
          </label>
          <div className={fieldWrapperClass}>
            <SweetDatePicker
              onChange={([start, end]) =>
                selectCustomRange({
                  start:
                    start != null
                      ? moment(start).tz(userTz).startOf('day').toISOString()
                      : '',
                  end:
                    end != null
                      ? moment(end).tz(userTz).endOf('day').toISOString()
                      : '',
                })
              }
              value={convertToBrowserTz(customRange, userTz)}
              options={{
                position: 'above',
                mode: 'range',
                ...options,
              }}
            >
              <input
                id="custom-date-range-field"
                data-input={true}
                readOnly={true}
                placeholder={gt('Click to select...')}
                className="form-control"
                type="text"
              />
            </SweetDatePicker>
          </div>
        </div>
      ) : selectedRangeTemplate === 'all-time' ? (
        ''
      ) : (
        <div className="form-group row">
          <div className={labelClass} />
          <div
            className={cs(
              fieldWrapperClass,
              'text-center font-weight-bold my-2'
            )}
          >
            {getCustomRangeTitle()}
          </div>
        </div>
      )}
    </div>
  );
}

const convertToBrowserTz = (
  range: DateRangeType,
  tz: string
): [start: string, end: string] => {
  /*
    Strip TZ info and send plain date objects to Flatpickr as it
    will awalys use the TZ set in the browser
    */
  const start =
    range.start !== ''
      ? moment(moment(range.start).tz(tz).format('YYYY-MM-DD')).toISOString()
      : '';
  const end =
    range.end !== ''
      ? moment(moment(range.end).tz(tz).format('YYYY-MM-DD')).toISOString()
      : '';
  return [start, end];
};

export type {VALID_RANGE_TEMPLATE, DateRangeType};
